import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ChevronDown, X, Calendar as CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { useVendors } from "@/hooks/useVendors";
import { useServiceCategories, useServiceSubCategories, useServiceTypes } from "@/hooks/useServiceCategories";

interface FilterPanelProps {
  type: 'event' | 'tailor-made' | 'resource' | 'benefit' | 'services';
  filters: Record<string, any>;
  onFiltersChange: (newFilters: Record<string, any>) => void;
}

interface FilterSectionProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

function FilterSection({ title, children, defaultOpen = true }: FilterSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="flex items-center justify-between w-full py-2 px-3 hover:bg-accent rounded-md transition-colors">
        <span className="text-sm font-medium">{title}</span>
        <ChevronDown className={cn("h-4 w-4 transition-transform", isOpen && "rotate-180")} />
      </CollapsibleTrigger>
      <CollapsibleContent className="px-3 py-2 space-y-3">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}

export default function FilterPanel({ type, filters, onFiltersChange }: FilterPanelProps) {
  // Fetch vendors using the proper hook
  const { vendors = [], isLoading: vendorsLoading } = useVendors();

  // Fetch service categories, subcategories, and types for services filter
  const { data: serviceCategories = [], isLoading: categoriesLoading } = useServiceCategories(false, true);
  const { data: serviceSubCategories = [], isLoading: subcategoriesLoading } = useServiceSubCategories(filters?.serviceCategoryId);
  const { data: serviceTypes = [], isLoading: serviceTypesLoading } = useServiceTypes();

  // Fetch categories (for event offerings)
  const { data: categories = [] } = useQuery<any[]>({
    queryKey: ["offering-categories"],
    queryFn: async () => {
      // For now, return empty array until proper categories endpoint is implemented
      return [];
    },
    enabled: type === 'event',
  });

  const handleFilterChange = (key: string, value: any) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const handleClearAll = () => {
    onFiltersChange({});
  };

  const hasActiveFilters = Object.keys(filters).length > 0;

  const renderEventFilters = () => (
    <>
      <FilterSection title="Vendor">
        <Select value={filters.vendorId || "all"} onValueChange={(value) => handleFilterChange('vendorId', value === "all" ? undefined : value)}>
          <SelectTrigger>
            <SelectValue placeholder="All vendors" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All vendors</SelectItem>
            {vendors.map((vendor) => (
              <SelectItem key={vendor.id} value={vendor.id.toString()}>
                {vendor.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FilterSection>

      <FilterSection title="Category">
        <div className="space-y-2">
          {categories.map((category) => (
            <div key={category.id} className="flex items-center space-x-2">
              <Checkbox
                id={`category-${category.id}`}
                checked={filters.categories?.includes(category.id) || false}
                onCheckedChange={(checked) => {
                  const currentCategories = filters.categories || [];
                  const newCategories = checked
                    ? [...currentCategories, category.id]
                    : currentCategories.filter((id: number) => id !== category.id);
                  handleFilterChange('categories', newCategories.length > 0 ? newCategories : undefined);
                }}
              />
              <Label htmlFor={`category-${category.id}`} className="text-sm cursor-pointer">
                {category.name}
              </Label>
            </div>
          ))}
        </div>
      </FilterSection>

      <FilterSection title="Price Range">
        <div className="space-y-2">
          <div>
            <Label htmlFor="min-price" className="text-xs">Min Price</Label>
            <Input
              id="min-price"
              type="number"
              placeholder="0"
              value={filters.minPrice || ""}
              onChange={(e) => handleFilterChange('minPrice', e.target.value || undefined)}
            />
          </div>
          <div>
            <Label htmlFor="max-price" className="text-xs">Max Price</Label>
            <Input
              id="max-price"
              type="number"
              placeholder="10000"
              value={filters.maxPrice || ""}
              onChange={(e) => handleFilterChange('maxPrice', e.target.value || undefined)}
            />
          </div>
        </div>
      </FilterSection>

      <FilterSection title="Status">
        <div className="flex items-center justify-between">
          <Label htmlFor="enabled-only" className="text-sm">Enabled only</Label>
          <Switch
            id="enabled-only"
            checked={filters.enabledOnly || false}
            onCheckedChange={(checked) => handleFilterChange('enabledOnly', checked || undefined)}
          />
        </div>
      </FilterSection>

      <FilterSection title="Visibility">
        <div>
          <Label htmlFor="max-visibility" className="text-xs">Max clients visible to</Label>
          <Input
            id="max-visibility"
            type="number"
            placeholder="e.g., 5"
            value={filters.maxVisibility || ""}
            onChange={(e) => handleFilterChange('maxVisibility', e.target.value || undefined)}
          />
        </div>
      </FilterSection>
    </>
  );

  const renderTailorMadeFilters = () => (
    <>
      <FilterSection title="Vendor">
        <Select value={filters.vendorId || "all"} onValueChange={(value) => handleFilterChange('vendorId', value === "all" ? undefined : value)}>
          <SelectTrigger>
            <SelectValue placeholder="All vendors" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All vendors</SelectItem>
            {vendors.map((vendor) => (
              <SelectItem key={vendor.id} value={vendor.id.toString()}>
                {vendor.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FilterSection>

      <FilterSection title="Request Date">
        <div className="space-y-2">
          <div>
            <Label className="text-xs">From</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-left font-normal">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {filters.dateFrom ? format(new Date(filters.dateFrom), "PPP") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={filters.dateFrom ? new Date(filters.dateFrom) : undefined}
                  onSelect={(date) => handleFilterChange('dateFrom', date ? date.toISOString() : undefined)}
                />
              </PopoverContent>
            </Popover>
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-left font-normal">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {filters.dateTo ? format(new Date(filters.dateTo), "PPP") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={filters.dateTo ? new Date(filters.dateTo) : undefined}
                  onSelect={(date) => handleFilterChange('dateTo', date ? date.toISOString() : undefined)}
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </FilterSection>

      <FilterSection title="Status">
        <div className="space-y-2">
          {['new', 'negotiation', 'approved', 'rejected'].map((status) => (
            <div key={status} className="flex items-center space-x-2">
              <Checkbox
                id={`status-${status}`}
                checked={filters.statuses?.includes(status) || false}
                onCheckedChange={(checked) => {
                  const currentStatuses = filters.statuses || [];
                  const newStatuses = checked
                    ? [...currentStatuses, status]
                    : currentStatuses.filter((s: string) => s !== status);
                  handleFilterChange('statuses', newStatuses.length > 0 ? newStatuses : undefined);
                }}
              />
              <Label htmlFor={`status-${status}`} className="text-sm cursor-pointer capitalize">
                {status}
              </Label>
            </div>
          ))}
        </div>
      </FilterSection>
    </>
  );

  const renderResourceFilters = () => (
    <>
      <FilterSection title="Vendor">
        <Select value={filters.vendorId || "all"} onValueChange={(value) => handleFilterChange('vendorId', value === "all" ? undefined : value)}>
          <SelectTrigger>
            <SelectValue placeholder="All vendors" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All vendors</SelectItem>
            {vendors.map((vendor) => (
              <SelectItem key={vendor.id} value={vendor.id.toString()}>
                {vendor.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FilterSection>

      <FilterSection title="Capacity Range">
        <div className="space-y-2">
          <div>
            <Label htmlFor="min-capacity" className="text-xs">Min Capacity</Label>
            <Input
              id="min-capacity"
              type="number"
              placeholder="1"
              value={filters.minCapacity || ""}
              onChange={(e) => handleFilterChange('minCapacity', e.target.value || undefined)}
            />
          </div>
          <div>
            <Label htmlFor="max-capacity" className="text-xs">Max Capacity</Label>
            <Input
              id="max-capacity"
              type="number"
              placeholder="100"
              value={filters.maxCapacity || ""}
              onChange={(e) => handleFilterChange('maxCapacity', e.target.value || undefined)}
            />
          </div>
        </div>
      </FilterSection>

      <FilterSection title="Slot Duration">
        <div className="space-y-2">
          {['30', '60', '90', '120'].map((duration) => (
            <div key={duration} className="flex items-center space-x-2">
              <Checkbox
                id={`duration-${duration}`}
                checked={filters.durations?.includes(duration) || false}
                onCheckedChange={(checked) => {
                  const currentDurations = filters.durations || [];
                  const newDurations = checked
                    ? [...currentDurations, duration]
                    : currentDurations.filter((d: string) => d !== duration);
                  handleFilterChange('durations', newDurations.length > 0 ? newDurations : undefined);
                }}
              />
              <Label htmlFor={`duration-${duration}`} className="text-sm cursor-pointer">
                {duration} minutes
              </Label>
            </div>
          ))}
        </div>
      </FilterSection>
    </>
  );

  const renderServicesFilters = () => (
    <>
      <FilterSection title="Search">
        <Input
          placeholder="Search services..."
          value={filters.search || ""}
          onChange={(e) => handleFilterChange('search', e.target.value || undefined)}
        />
      </FilterSection>

      <FilterSection title="Vendor">
        <Select value={filters.vendorId || "all"} onValueChange={(value) => handleFilterChange('vendorId', value === "all" ? undefined : value)}>
          <SelectTrigger>
            <SelectValue placeholder="All vendors" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All vendors</SelectItem>
            {vendorsLoading ? (
              <SelectItem value="loading" disabled>Loading vendors...</SelectItem>
            ) : (
              vendors.map((vendor) => (
                <SelectItem key={vendor.id} value={vendor.id}>
                  {vendor.name}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </FilterSection>

      <FilterSection title="Service Category">
        <Select
          value={filters.serviceCategoryId || "all"}
          onValueChange={(value) => {
            const categoryId = value === "all" ? undefined : value;
            handleFilterChange('serviceCategoryId', categoryId);
            // Clear subcategory when category changes
            if (filters.serviceSubCategoryId) {
              handleFilterChange('serviceSubCategoryId', undefined);
            }
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categoriesLoading ? (
              <SelectItem value="loading" disabled>Loading categories...</SelectItem>
            ) : (
              serviceCategories.map((category) => (
                <SelectItem key={category.id} value={category.id}>
                  {category.name}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </FilterSection>

      <FilterSection title="Service Subcategory">
        <Select
          value={filters.serviceSubCategoryId || "all"}
          onValueChange={(value) => handleFilterChange('serviceSubCategoryId', value === "all" ? undefined : value)}
          disabled={!filters.serviceCategoryId}
        >
          <SelectTrigger>
            <SelectValue placeholder={filters.serviceCategoryId ? "All subcategories" : "Select category first"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All subcategories</SelectItem>
            {subcategoriesLoading ? (
              <SelectItem value="loading" disabled>Loading subcategories...</SelectItem>
            ) : (
              serviceSubCategories.map((subcategory) => (
                <SelectItem key={subcategory.id} value={subcategory.id}>
                  {subcategory.name}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </FilterSection>

      <FilterSection title="Service Type">
        <Select value={filters.serviceTypeId || "all"} onValueChange={(value) => handleFilterChange('serviceTypeId', value === "all" ? undefined : value)}>
          <SelectTrigger>
            <SelectValue placeholder="All service types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All service types</SelectItem>
            {serviceTypesLoading ? (
              <SelectItem value="loading" disabled>Loading service types...</SelectItem>
            ) : (
              serviceTypes.map((serviceType) => (
                <SelectItem key={serviceType.id} value={serviceType.id}>
                  {serviceType.name} ({serviceType.behaviorType})
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </FilterSection>

      <FilterSection title="Status">
        <div className="flex items-center justify-between">
          <Label htmlFor="active-only" className="text-sm">Active only</Label>
          <Switch
            id="active-only"
            checked={filters.isActive || false}
            onCheckedChange={(checked) => handleFilterChange('isActive', checked || undefined)}
          />
        </div>
      </FilterSection>
    </>
  );

  const renderBenefitFilters = () => (
    <>
      <FilterSection title="Vendor">
        <Select value={filters.vendorId || "all"} onValueChange={(value) => handleFilterChange('vendorId', value === "all" ? undefined : value)}>
          <SelectTrigger>
            <SelectValue placeholder="All vendors" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All vendors</SelectItem>
            {vendors.map((vendor) => (
              <SelectItem key={vendor.id} value={vendor.id.toString()}>
                {vendor.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FilterSection>

      <FilterSection title="Discount Type">
        <div className="space-y-2">
          {['percentage', 'fixed'].map((discountType) => (
            <div key={discountType} className="flex items-center space-x-2">
              <Checkbox
                id={`discount-${discountType}`}
                checked={filters.discountTypes?.includes(discountType) || false}
                onCheckedChange={(checked) => {
                  const currentTypes = filters.discountTypes || [];
                  const newTypes = checked
                    ? [...currentTypes, discountType]
                    : currentTypes.filter((t: string) => t !== discountType);
                  handleFilterChange('discountTypes', newTypes.length > 0 ? newTypes : undefined);
                }}
              />
              <Label htmlFor={`discount-${discountType}`} className="text-sm cursor-pointer capitalize">
                {discountType}
              </Label>
            </div>
          ))}
        </div>
      </FilterSection>

      <FilterSection title="Expiry Date">
        <div className="space-y-2">
          <div>
            <Label className="text-xs">From</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-left font-normal">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {filters.expiryFrom ? format(new Date(filters.expiryFrom), "PPP") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={filters.expiryFrom ? new Date(filters.expiryFrom) : undefined}
                  onSelect={(date) => handleFilterChange('expiryFrom', date ? date.toISOString() : undefined)}
                />
              </PopoverContent>
            </Popover>
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-left font-normal">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {filters.expiryTo ? format(new Date(filters.expiryTo), "PPP") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={filters.expiryTo ? new Date(filters.expiryTo) : undefined}
                  onSelect={(date) => handleFilterChange('expiryTo', date ? date.toISOString() : undefined)}
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </FilterSection>
    </>
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Filters</CardTitle>
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearAll}
              className="h-auto p-1 text-xs"
            >
              <X className="h-3 w-3 mr-1" />
              Clear all
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-1">
        {type === 'event' && renderEventFilters()}
        {type === 'services' && renderServicesFilters()}
        {type === 'tailor-made' && renderTailorMadeFilters()}
        {type === 'resource' && renderResourceFilters()}
        {type === 'benefit' && renderBenefitFilters()}
      </CardContent>
    </Card>
  );
}